<template>
  <TransitionExpand>
    <div v-if="injectionId && injection" style="border-top: 1px solid transparent">
      <div class="card-rounded card-rounded--hoverable sample-injection-mini">
        <div class="left flex mr-4">
          <div class="flex__left mt-1">
            <InjectionLinkComponent :injection="injection" />

            <div class="mt-4">
              <span class="mr-4">
                <template v-if="injection.type === 'standard'"> Standard injection </template>
                <template v-else> Sample injection </template>
              </span>

              <span class="span-date mr-4" v-html="formatTime(injection.date_started)" />

              <LoadingComponent
                v-if="!updated"
                :label="null"
                class="loading-component--sm-inline mt-1"
              />
              <span v-else-if="injection.state !== 'REA' && error" class="text-red">
                <i class="material-icons material-icon--16">error</i>
                {{ error }}
              </span>
              <StatesForEntity
                v-else
                :state="injection.state"
                data-test-id="sample-injection-state"
              />
            </div>
          </div>

          <div class="flex__right mt-2">
            <ProgressComponent
              v-if="injection != null && injection.state === 'RUN'"
              class="progress"
              :label="injection.progress_time_str"
              :progress="injection.progress"
            />
          </div>
        </div>
        <button
          v-if="injection.state === 'PRE' || injection.state === 'RUN'"
          v-tippy
          content="Stop current run"
          class="button--square-image right"
          @click.stop="stop"
        >
          <i class="material-icons material-icon--18">stop</i>
          Stop
        </button>
      </div>
    </div>
  </TransitionExpand>
</template>

<script>
  import SampleInjectionSocket from 'api/sockets/SampleInjectionSocket';
  import LoadingComponent from 'components/element/LoadingComponent';
  import StatesForEntity from '@/uikitProject/states/packs/StatesForEntity';
  import ProgressComponent from 'components/element/ProgressComponent';
  import InjectionLinkComponent from 'components/element/InjectionLinkComponent';
  import faviconService, { FaviconStatuses } from '@/services/FaviconService';
  import { isInjectionFailed, isInjectionInProgress } from 'utils/injectionHelpers';
  import { isBrowserTabActive } from 'utils/browserHelpers';
  import TransitionExpand from '@/uikitProject/transitions/TransitionExpand';
  import { apiInjections } from '@/api/graphql/cloud/injections';
  import { CHECK_CONNECTION_INTERVAL_MS } from '@/constants/injections/connection';
  import { INJECTION_ERROR_MESSAGES } from '@/constants/injections/errors';

  export default {
    name: 'SampleInjectionMiniComponent',

    components: {
      TransitionExpand,
      InjectionLinkComponent,
      ProgressComponent,
      StatesForEntity,
      LoadingComponent,
    },

    props: {
      injectionId: {
        type: Number,
      },
    },

    data: () => ({
      injectionSocket: null,
      injection: {},
      updated: null,
      timerConnectionLost: null,

      subscriptionForInjectionBrief: null,
    }),

    computed: {
      isInProgress() {
        return Boolean(this.injection) && isInjectionInProgress(this.injection);
      },

      error() {
        return (
          this.injection.error &&
          (INJECTION_ERROR_MESSAGES[this.injection.error] ?? 'Unknown error')
        );
      },
    },

    watch: {
      injectionId(valueNew, valueOld) {
        this.updated = false;

        if (valueNew) {
          this.initInjection(this.injectionId);
        } else if (valueOld) {
          this.showPreviousInjectionResult(valueOld);
        }
      },
      isInProgress: {
        handler(isInProgress) {
          if (isInProgress) {
            faviconService.setStatus(FaviconStatuses.RUNNING);
          }
        },
        immediate: true,
      },
    },

    created() {
      this.initInjection(this.injectionId);
    },

    beforeDestroy() {
      if (this.timerConnectionLost != null) clearTimeout(this.timerConnectionLost);

      this.destroyInjectionSocket();
      this.stopSubscriptions();

      if (isBrowserTabActive()) {
        faviconService.setStatus(FaviconStatuses.NONE);
      }
    },

    methods: {
      showPreviousInjectionResult(previousInjectionId) {
        /**
         * A device state can be updated a bit earlier than an injection is actually stopped.
         * So we need to make a delayed request to know the injection result.
         */
        setTimeout(async () => {
          if (!this.injectionId && !isBrowserTabActive()) {
            const state = await apiInjections.getInjectionState(previousInjectionId);

            if (!this.injectionId && !isInjectionInProgress({ state })) {
              faviconService.setStatus(
                isInjectionFailed({ state }) ? FaviconStatuses.ERROR : FaviconStatuses.COMPLETED,
              );
              this.injection = null;
            }
          } else {
            if (isBrowserTabActive()) {
              faviconService.setStatus(FaviconStatuses.NONE);
            }
          }
        }, 1000);
      },

      createInjectionSocket(id) {
        this.destroyInjectionSocket();
        return SampleInjectionSocket.start(id);
      },
      destroyInjectionSocket() {
        if (this.injectionSocket != null) {
          this.injectionSocket?.close(this.listenersGroupId);
        }
      },

      onInjection(i) {
        this.injection = { ...this.injection, ...i };
        this.updated = true;
      },

      stop() {
        this.showNotificationIfRpcError(() => this.injectionSocket.stop());
      },

      initInjection(injectionId) {
        if (!injectionId) {
          return;
        }

        this.destroyInjectionSocket();
        this.stopSubscriptions();

        this.injection = null;

        faviconService.setStatus(FaviconStatuses.NONE);

        this.injectionSocket = this.createInjectionSocket(injectionId);

        this.initSubscriptionForInjectionBrief(injectionId);
      },
      stopSubscriptions() {
        this.subscriptionForInjectionBrief?.unsubscribe();
      },

      async initSubscriptionForInjectionBrief(injectionId) {
        this.subscriptionForInjectionBrief = await apiInjections.subscribeForInjectionBrief(
          injectionId,
          (injection) => {
            console.warn('event injection brief', injection);
            this.onInjection(injection);
            this.onEvent();
          },
        );
      },

      onEvent() {
        if (this.timerConnectionLost != null) {
          clearTimeout(this.timerConnectionLost);
        }

        if (this.isInjectionInProgress) {
          this.timerConnectionLost = setTimeout(
            () => this.setNoInternetState(),
            CHECK_CONNECTION_INTERVAL_MS,
          );
        }
      },

      setNoInternetState() {
        this.injection.state = '@';
      },
    },
  };
</script>

<style lang="scss" scoped>
  .sample-injection-mini {
    width: 100%;
    padding: 24px;

    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    align-items: center;

    .left {
      flex-grow: 1;
      flex-shrink: 1;
    }

    .right {
      flex-shrink: 0;
    }
  }

  .flex {
    display: flex;
    flex-direction: column;

    .flex__right {
      .progress {
        margin: initial;
        width: 218px;
      }
    }
  }
  @screen sm {
    .flex {
      display: flex;
      flex-direction: row;
      flex-wrap: wrap;
      align-items: flex-end;

      .flex__left {
        flex-grow: 0;
        flex-shrink: 1;
      }

      .flex__right {
        flex-grow: 1;

        .progress {
          margin: auto;
        }
      }
    }
  }
</style>
